/**
 * The entry point.
 *
 * Three stylesheets, in this order and for a reason:
 *
 * 1. `tokens.generated.css` — the `--tp-*` custom properties, emitted from `packages/design-tokens` by
 *    `tools/build-web-tokens.mjs`. Generated and committed, so a build has no step that can fail and a
 *    diff shows a token change.
 * 2. `base.css` — the reset and the shell.
 * 3. `screens.css` — the controls, which read the tokens.
 *
 * The order matters because the later files use the earlier ones' variables; a custom property must
 * exist before something references it.
 */

import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { BrowserRouter } from "react-router-dom";

import { App } from "./App.tsx";
import "./styles/tokens.generated.css";
import "./styles/base.css";
/*
 * `ui.css` before `screens.css`, and the order is the dependency order: the kit defines the controls,
 * and a screen's stylesheet only lays them out. Reversing it would let a screen's layout rule win over
 * a kit's own styling by accident, which is the first step towards two buttons that look different.
 */
import "./styles/ui.css";
import "./styles/screens.css";
import { initTheme } from "./lib/theme.ts";

initTheme();

const container = document.getElementById("root");
if (container === null) {
  // Only reachable if `index.html` lost its root element, which is a build mistake rather than a
  // runtime condition — so it fails loudly instead of rendering nothing.
  throw new Error("#root is missing from index.html");
}

createRoot(container).render(
  /*
   * `StrictMode` double-invokes effects in development, which is deliberate here: the session restore
   * is guarded by a ref, so a double invocation must not fire two refreshes — and two concurrent
   * refreshes are exactly what triggers the API's reuse detection. Running the guard under
   * `StrictMode` is the cheapest way to keep that true.
   */
  <StrictMode>
    <BrowserRouter>
      <App />
    </BrowserRouter>
  </StrictMode>,
);
